import cn from 'classnames';
import { FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { LocationStateType, PATHS } from '@/shared/config';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import {
  useFDA2DRestrictions,
  useFDA3DGPRestrictions,
} from '@/features/fdaRestrictions';
import { useSignReport } from '@/features/signReport';

import { PreviewType } from '../../config';

import styles from './ReportActions.module.scss';

type ReportActionsProps = {
  className?: string;
  patientID: string;
  reportID: string;
  previewType?: PreviewType;
};

export const ReportActions = (props: ReportActionsProps) => {
  const {
    patientID,
    reportID,
    className,
    previewType = PreviewType.Preview2D,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, isPhone } = useMedia();

  const canChangeToothApproved = useAppSelector(
    reportsModel.selectors.selectCurrentReportCanChangeToothApproved,
  );
  const reportSignStatus = useAppSelector(
    reportsModel.selectors.selectCurrentReportSignStatus,
  );
  const { isFDAVersion2 } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );
  const allROITeethIsApproved = useAppSelector(
    toothModel.selectors.selectAllTeethIsApproved(reportID),
  );
  // FDA 2.0 treats every report tooth as active, so approval is checked
  // across all teeth, not only the ROI ones
  const allReportTeethIsApproved = useAppSelector(
    toothModel.selectors.selectIsAllReportTeethApproved(
      reportID,
      isFDAVersion2,
    ),
  );

  const isReportSigned = reportSignStatus === 'signed';

  const { shouldRestrictPrint: shouldRestrictPrint3D } =
    useFDA3DGPRestrictions();
  const { shouldRestrictPrint: shouldRestrictPrint2D, isFDA2DReport } =
    useFDA2DRestrictions();

  const shouldRestrictPrint = shouldRestrictPrint3D || shouldRestrictPrint2D;

  const allTeethIsApproved = isFDA2DReport
    ? allReportTeethIsApproved
    : allROITeethIsApproved;

  const buttonSize = isPhone ? 'medium' : 'large';

  const getPrintButtonIcon = () => {
    if (isMobile) {
      return 'print';
    }
    if (isReportSigned) {
      return 'check';
    }
    return undefined;
  };

  const renderPrintButtonText = () => {
    if (isMobile && !isReportSigned) {
      return null;
    }

    if (isReportSigned) {
      return (
        <FormattedMessage
          id="report.printReport"
          defaultMessage="Print report"
        />
      );
    }

    return (
      <FormattedMessage
        id="report.printReportWithoutSignature"
        defaultMessage="Print report without signature"
      />
    );
  };

  const getPrintButtonVariant = () => {
    if (isReportSigned) {
      return 'primary';
    }
    if (isMobile) {
      return 'secondary';
    }
    return 'tertiary';
  };

  const openConfirmSignReportModal = useSignReport(allTeethIsApproved);

  const handlePrintButton = () => {
    trackEvent({
      eventName: isReportSigned
        ? 'ReportPagePrintReportButtonClicked'
        : 'ReportPagePrintReportWithoutSignatureButtonClicked',
    });

    const locationState = location?.state as LocationStateType;

    const path =
      previewType === PreviewType.Preview3D
        ? generatePath(PATHS.previewReport, { patientID, reportID })
        : generatePath(PATHS.patient + '/' + PATHS.preview2D, {
            patientID,
            reportID,
          });

    navigate(path, {
      state: {
        ...locationState,
        lastPositionReport: window.pageYOffset,
        toothID: undefined,
      },
    });
  };

  return (
    <footer
      className={cn(styles.container, className)}
      data-section="report-actions"
    >
      {!shouldRestrictPrint && (
        <Button
          data-component="print-report-button"
          variant={getPrintButtonVariant()}
          onClick={handlePrintButton}
          icon={getPrintButtonIcon()}
          className={isReportSigned ? styles.fullWidthButton : undefined}
          size={buttonSize}
        >
          {renderPrintButtonText()}
        </Button>
      )}

      {!isReportSigned && (
        <Button
          data-component="sign-report-button"
          icon="check"
          disabled={
            reportSignStatus === 'withoutSign' ||
            !canChangeToothApproved ||
            (isFDA2DReport && !allTeethIsApproved)
          }
          onClick={openConfirmSignReportModal}
          className={styles.fullWidthButton}
          size={buttonSize}
        >
          {allTeethIsApproved || isFDA2DReport ? (
            <FormattedMessage id="report.sign" defaultMessage="Sign" />
          ) : (
            <FormattedMessage
              id="report.approveAndSign"
              defaultMessage="Approve all and sign"
            />
          )}
        </Button>
      )}
    </footer>
  );
};
